'use client';

import type {
  AiApplicationComponentsResponseDto,
  AiComponentDto,
} from '@terra/shared-contracts-ai-applications';
import { resolveAiComponentDisplayDescription } from '@terra/shared-contracts-ai-applications';
import {
  Badge,
  CustomDrawer,
  DrawerCollapsible,
  DrawerGeneralDetails,
  MarkdownPreview,
} from '@terra/ui-design-system';
import { Box, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AI_COMPONENT_TYPE_KEYS } from '../utils/aiComponentTypeLabels';
import {
  AI_COMPONENT_TYPE_ICON_COLORS,
  getAiComponentTypeIcon,
} from '../utils/aiComponentIcons';
import {
  getAiComponentInputSchema,
  getAiComponentMetadataString,
} from '../utils/aiComponentMetadata';
import { resolveAiComponentVerificationBadge } from '../utils/aiComponentVerification';
import { isToolFamilyComponentType } from '../utils/isUserFacingAgent';
import { AiApplicationMcpComponentDetails } from './AiApplicationMcpComponentDetails';
import { AiComponentVerificationEvidence } from './AiComponentVerificationEvidence';
import { RelatedAiComponentsDrawerTable } from './RelatedAiComponentsDrawerTable';

export type AiApplicationComponentDrawerSlots = {
  detectionsTab?: ReactNode;
  apiDetailsTab?: ReactNode;
  apiDetailsTabLabel?: string;
  extraOverviewSections?: ReactNode;
  formatUpdatedAt?: (value: Date | string) => string;
};

type AiApplicationComponentDrawerProps = {
  component: AiComponentDto | null;
  components: AiComponentDto[];
  relations: AiApplicationComponentsResponseDto['relations'];
  isOpen: boolean;
  onClose: () => void;
  descriptionFallback?: string;
  slots?: AiApplicationComponentDrawerSlots;
};

export function AiApplicationComponentDrawer({
  component,
  components,
  relations,
  isOpen,
  onClose,
  descriptionFallback,
  slots,
}: AiApplicationComponentDrawerProps) {
  const t = useTranslations('aiApplications.componentDrawer');
  const tShared = useTranslations('aiApplications.agentDrilldown');
  const tCommon = useTranslations('common');
  const tComponentTypes = useTranslations('aiApplications.componentTypes');
  const tVerification = useTranslations('aiApplications.assets.verification');
  const [selectedTab, setSelectedTab] = useState('overview');
  const hasApiDetailsTab = Boolean(slots?.apiDetailsTab);

  const relatedRelations = useMemo(() => {
    if (!component) return [];

    return relations.filter(
      (relation) =>
        relation.sourceId === component.id ||
        relation.targetId === component.id,
    );
  }, [component, relations]);

  const relatedComponents = useMemo(() => {
    if (!component) return [];

    const relatedIds = new Set(
      relatedRelations.map((relation) =>
        relation.sourceId === component.id
          ? relation.targetId
          : relation.sourceId,
      ),
    );

    return components.filter((item) => relatedIds.has(item.id));
  }, [component, components, relatedRelations]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedTab('overview');
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedTab('overview');
  }, [component?.id]);

  useEffect(() => {
    if (selectedTab === 'apiDetails' && !hasApiDetailsTab) {
      setSelectedTab('overview');
    }
  }, [hasApiDetailsTab, selectedTab]);

  if (!component) return null;

  const getTypeLabel = (type: AiComponentDto['type']) =>
    tComponentTypes(AI_COMPONENT_TYPE_KEYS[type]);

  const formatProvenance = (provenance: AiComponentDto['provenance']) => {
    if (!provenance) {
      return tShared('unknownRole');
    }

    return t(`provenanceValues.${provenance}`);
  };

  const formatSources = (sources: AiComponentDto['sources']) => {
    if (sources.length === 0) {
      return tShared('noInterface');
    }

    return sources.map((source) => t(`sourceValues.${source}`)).join(', ');
  };

  const formatUpdatedAt = (value: Date | string) => {
    if (slots?.formatUpdatedAt) {
      return slots.formatUpdatedAt(value);
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return tCommon('na');
    }

    return date.toLocaleDateString();
  };

  const Icon = getAiComponentTypeIcon(component.type);
  const iconColors = AI_COMPONENT_TYPE_ICON_COLORS[component.type];
  const isMcpComponentType =
    component.type === 'MCP_TOOL' ||
    component.type === 'MCP_PROTOCOL' ||
    component.type === 'MCP_PROMPT' ||
    component.type === 'MCP_RESOURCE';
  const verificationBadge = resolveAiComponentVerificationBadge({ component });
  const toolInputSchema = getAiComponentInputSchema({
    metadata: component.metadata,
    runtimeOverlay: component.runtimeOverlay,
  });
  const toolInputSchemaSource =
    toolInputSchema !== null
      ? `\`\`\`json\n${JSON.stringify(toolInputSchema, null, 2)}\n\`\`\``
      : null;
  const discriminator = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'discriminator',
  });
  const howToInvoke = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'stimulus',
  });
  const whatProvesIt = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'predictedSignal',
  });
  const invocationVisibility = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'invocationVisibility',
  });
  const answerShape = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'answerShape',
  });
  const attackGoal = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'attackGoal',
  });
  const guardrailDecision = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'guardrailDecision',
  });
  const guardrailMatch = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'guardrailMatch',
  });
  const guardrailGap = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'guardrailGap',
  });
  const invocationVisibilityLabel =
    invocationVisibility === 'user_visible'
      ? t('verification.invocationUserVisible')
      : invocationVisibility === 'hidden'
        ? t('verification.invocationHidden')
        : invocationVisibility === 'unknown'
          ? t('verification.invocationUnknown')
          : null;
  const guardrailDecisionLabel =
    guardrailDecision === 'pattern'
      ? t('guardrailDecisionValues.pattern')
      : guardrailDecision === 'model'
        ? t('guardrailDecisionValues.model')
        : guardrailDecision === 'prompt'
          ? t('guardrailDecisionValues.prompt')
          : guardrailDecision;
  const codeAnalysisRows = [
    discriminator
      ? {
          title: t('discriminator'),
          hint: t('fieldHints.discriminator'),
          value: discriminator,
          fullWidth: true,
        }
      : null,
    howToInvoke
      ? {
          title: t('howToInvoke'),
          hint: t('fieldHints.howToInvoke'),
          value: howToInvoke,
          fullWidth: true,
        }
      : null,
    whatProvesIt
      ? {
          title: t('whatProvesIt'),
          hint: t('fieldHints.whatProvesIt'),
          value: whatProvesIt,
          fullWidth: true,
        }
      : null,
    invocationVisibilityLabel
      ? {
          title: t('invocationVisibility'),
          hint: t('fieldHints.invocationVisibility'),
          value: invocationVisibilityLabel,
          fullWidth: true,
        }
      : null,
    answerShape
      ? {
          title: t('answerShape'),
          hint: t('fieldHints.answerShape'),
          value: answerShape,
          fullWidth: true,
        }
      : null,
    attackGoal
      ? {
          title: t('attackGoal'),
          hint: t('fieldHints.attackGoal'),
          value: attackGoal,
          fullWidth: true,
        }
      : null,
    guardrailDecisionLabel
      ? {
          title: t('guardrailDecision'),
          hint: t('fieldHints.guardrailDecision'),
          value: guardrailDecisionLabel,
          fullWidth: true,
        }
      : null,
    guardrailMatch && guardrailMatch !== howToInvoke
      ? {
          title: t('guardrailMatch'),
          hint: t('fieldHints.guardrailMatch'),
          value: guardrailMatch,
          fullWidth: true,
        }
      : null,
    guardrailGap
      ? {
          title: t('guardrailGap'),
          hint: t('fieldHints.guardrailGap'),
          value: guardrailGap,
          fullWidth: true,
        }
      : null,
  ].filter((row) => row !== null);

  return (
    <CustomDrawer
      isOpen={isOpen}
      onClose={onClose}
      header={{
        topic: t('topic'),
        title: component.name,
        description:
          resolveAiComponentDisplayDescription({
            description: component.description,
            metadata: component.metadata,
            type: component.type,
            fallback: descriptionFallback,
          }) ?? descriptionFallback,
        iconConfig: {
          iconElement: (
            <Box
              bg={iconColors.bg}
              borderWidth="1px"
              borderColor={iconColors.border}
              color={iconColors.color}
              borderRadius="md"
              p={1.5}
              flexShrink={0}
            >
              <Icon size={22} />
            </Box>
          ),
        },
      }}
      tabs={[
        { value: 'overview', label: tShared('tabs.overview') },
        ...(hasApiDetailsTab
          ? [
              {
                value: 'apiDetails',
                label: slots?.apiDetailsTabLabel ?? tShared('tabs.overview'),
              },
            ]
          : []),
        ...(slots?.detectionsTab
          ? [{ value: 'detections', label: t('tabs.detections') }]
          : []),
      ]}
      selectedTab={selectedTab}
      onTabChange={setSelectedTab}
    >
      {selectedTab === 'detections' && slots?.detectionsTab ? (
        slots.detectionsTab
      ) : selectedTab === 'apiDetails' && slots?.apiDetailsTab ? (
        slots.apiDetailsTab
      ) : (
        <VStack gap={8} alignItems="stretch">
          <DrawerGeneralDetails
            title={tShared('generalDetails')}
            rows={[
              [
                {
                  title: t('provenance'),
                  hint: t('fieldHints.provenance'),
                  value: formatProvenance(component.provenance),
                },
                {
                  title: t('verificationStatus'),
                  hint: t('fieldHints.verification'),
                  value: (
                    <Badge variant="outline">
                      {tVerification(verificationBadge)}
                    </Badge>
                  ),
                },
              ],
              [
                {
                  title: t('sources'),
                  hint: t('fieldHints.sources'),
                  value: formatSources(component.sources),
                },
              ],
              [
                {
                  title: tShared('type'),
                  hint: t('fieldHints.type'),
                  value: getTypeLabel(component.type),
                },
                {
                  title: tShared('coverage'),
                  hint: t('fieldHints.coverage'),
                  value: (
                    <Badge
                      colorPalette={component.isInternal ? 'gray' : 'blue'}
                    >
                      {component.isInternal
                        ? tShared('internal')
                        : tShared('external')}
                    </Badge>
                  ),
                },
              ],
              [
                {
                  title: tShared('role'),
                  hint: t('fieldHints.role'),
                  value: component.role ?? tShared('unknownRole'),
                },
                {
                  title: tShared('relationships'),
                  hint: t('fieldHints.relationships'),
                  value: `${relatedRelations.length}`,
                },
              ],
              [
                {
                  title: t('interfaceType'),
                  hint: t('fieldHints.interfaceType'),
                  value: component.interfaceType ?? tShared('noInterface'),
                },
                {
                  title: tShared('updated'),
                  hint: t('fieldHints.updated'),
                  value: formatUpdatedAt(component.updatedAt),
                },
              ],
            ]}
          />

          {codeAnalysisRows.length > 0 ? (
            <DrawerGeneralDetails
              title={t('codeAnalysis')}
              rows={codeAnalysisRows.map((row) => [row])}
            />
          ) : null}

          {isMcpComponentType ? (
            <AiApplicationMcpComponentDetails component={component} />
          ) : null}

          <AiComponentVerificationEvidence component={component} />

          {isToolFamilyComponentType(component.type) &&
          toolInputSchemaSource ? (
            <DrawerCollapsible title={t('inputSchema')} defaultOpen>
              <Box w="full" borderRadius="md">
                <MarkdownPreview source={toolInputSchemaSource} compact />
              </Box>
            </DrawerCollapsible>
          ) : null}

          {slots?.extraOverviewSections}

          <RelatedAiComponentsDrawerTable
            relatedComponents={relatedComponents}
          />
        </VStack>
      )}
    </CustomDrawer>
  );
}
